import { ApiError } from "@/lib/api-client";
import { formatWaitMinutes, readRetryAfterSeconds } from "@/lib/retry-after";
import {
  CONTRACT_BLOCKED_BODY,
  CONTRACT_INACTIVE_TITLE,
} from "@/routes/org-admin/contract-notice";
import {
  INVALID_SIGN_IN_EMAIL_BODY,
  INVALID_SIGN_IN_EMAIL_TITLE,
} from "@/routes/org-admin/sign-in-email-notice";

// ONE mapper for every write the Edit-member modal hosts (Stories 14.4 + 14.5 +
// 14.6's seat removal). Night boundary #7 pins this layout: the mutations refuse
// for the SAME reasons — the target left the roster, the target is not a
// clinician, the org contract lapsed, the actor is hammering the mailer — and
// per-slice mappers would explain one refusal several ways in one dialog.
// Seat removal needed no new branch: its ORG-008 / ORG-009 / ORG-010 refusals
// are the ones already mapped here, which is the point of the shared checks.
//
// Mirror of invitation-error.ts / seat-request-error.ts: we branch ONLY on the
// structured `error.code` / `error.status`, NEVER on the server's raw `msg`.
//
// [Source: src/application/organizations/_shared/exceptions.py — 403 ORG-008
//  outside_org_scope, 403 ORG-009 insufficient_target_scope, 409 ORG-012
//  email_in_use; _shared/contract_guard.py — 403 ORG-010 contract_expired;
//  _shared/member_action_rate_limiter.py — 429 ORG-013; reset_clinician_password/
//  exceptions.py — 409 ORG-011 target_not_activated + 409 ORG-014
//  email_change_cooldown; _shared/exceptions.py — 422 ORG-015
//  invalid_email_format, 409 ORG-017 target_blocked, 409 ORG-019
//  target_access_record_missing.]

export interface MemberActionBanner {
  /** `info` = a settled state (no retry); `warn`/`danger` = check/transport. */
  variant: "info" | "warn" | "danger";
  title: string;
  body: string;
}

/**
 * ORG-011 in one string (14.5 night boundary #5). The invited row's password
 * reset is pre-gated in the UI AND refused by the server, so the disabled
 * affordance's accessible reason and the 409 banner MUST be the same sentence —
 * otherwise an admin who defeats the pre-gate (stale roster, two tabs) is given
 * a second, differently-worded explanation of one situation.
 */
export const TARGET_NOT_ACTIVATED_REASON =
  "This clinician hasn't activated their account yet, so there is no password to reset. Correct their email address instead if it's wrong.";

/**
 * ORG-017 in one string (Story 15.8, AC8 — the `TARGET_NOT_ACTIVATED_REASON`
 * precedent above, applied to the second refusal that has both a pre-gate and a
 * server gate). All four member write controls disable on a blocked row AND the
 * server refuses the same four calls, so the accessible reason on the disabled
 * control and the 409 banner have to be ONE sentence: an admin who defeats the
 * pre-gate — a stale roster, a second tab, a direct API call — must not be
 * handed a differently-worded account of one situation.
 *
 * It names CITS as the blocker and says the block is not the org_admin's to
 * lift, because the only useful next step is off this screen. The WORDING here
 * was a night undefined-axis decision; the SHARING is the criterion.
 */
export const TARGET_BLOCKED_REASON =
  "CITS has blocked this clinician's platform access. Only CITS can lift that block, so their account can't be changed from here.";

/**
 * The heading over {@link TARGET_BLOCKED_REASON}, exported for the same reason
 * the body is: the dialog renders the reason once and points all four disabled
 * controls at it, so it needs the heading too. Follows the
 * `CONTRACT_INACTIVE_TITLE` / `CONTRACT_BLOCKED_BODY` split this module already
 * imports from `contract-notice` — one pair of strings, two surfaces.
 */
export const TARGET_BLOCKED_TITLE = "This clinician's access is blocked";

const GENERIC: MemberActionBanner = {
  variant: "danger",
  title: "Couldn't save that change",
  body: "Something went wrong applying the change. Please try again.",
};

// ORG-008 — the target holds no active seat in the actor's org (an unknown
// user_id answers the same refusal by design). Settled roster state: the
// remedy is to reload the roster, not to retry the write.
const OUTSIDE_ORG_SCOPE: MemberActionBanner = {
  variant: "warn",
  title: "This clinician isn't on your roster",
  body: "They no longer hold an active seat in your organization, so the change wasn't applied. Refresh the roster to see the current members.",
};

// ORG-009 — the target is not a clinician account. Nothing the admin can fix by
// retrying; naming the rule is the whole message.
const INSUFFICIENT_TARGET_SCOPE: MemberActionBanner = {
  variant: "warn",
  title: "This account can't be edited here",
  body: "Only clinician accounts can be administered from this screen.",
};

// ORG-010 — commercial state, not a permission problem. Copy comes from the
// 14.12 shared const so this refusal reads identically to the dashboard banner
// the same admin is already looking at.
const CONTRACT_INACTIVE: MemberActionBanner = {
  variant: "warn",
  title: CONTRACT_INACTIVE_TITLE,
  body: CONTRACT_BLOCKED_BODY,
};

// ORG-011 — the still-invited NEW learner has no credentials yet.
const TARGET_NOT_ACTIVATED: MemberActionBanner = {
  variant: "info",
  title: "This clinician hasn't activated their account",
  body: TARGET_NOT_ACTIVATED_REASON,
};

// ORG-017 — CITS has blocked the target's platform access, so every member
// action on that row is refused (Story 15.8). `info` for the same reason
// ORG-011 is: nothing went wrong and nothing needs re-checking — this is a
// settled fact about the TARGET, and the remedy is off this screen entirely.
const TARGET_BLOCKED: MemberActionBanner = {
  variant: "info",
  title: TARGET_BLOCKED_TITLE,
  body: TARGET_BLOCKED_REASON,
};

// ORG-019 — the target has an active seat and no `authz.users` projection row:
// a data defect, not a state the product produces. It has its OWN branch because
// until the PR #731 review the server refused this as ORG-017, and this mapper
// dutifully told the admin CITS had blocked an account nobody had blocked.
//
// Two things the copy must NOT do. It must not name a block or point at CITS to
// LIFT one — no block happened, so that errand cannot end. And it must not read
// like `GENERIC`'s "please try again": nothing here clears by retrying, and an
// admin can follow that advice forever. Escalation is the only real next step,
// which is why the sentence says who to escalate to.
//
// `warn`, not `info`. ORG-011/ORG-014/ORG-017 are `info` because they are
// settled states working as designed; this one is something broken, and the
// variant is the only part of the banner that says so at a glance.
//
// No exported const and no pre-gate twin, unlike ORG-017: the roster CANNOT know.
// `get_org_admin_dashboard` renders a projection-less seat holder fail-soft as
// `account_state: "active"` (it logs `authz.user.missing` and shows the row), so
// there is no client-side state to disable a control on — this banner is the
// admin's first and only sight of the defect.
const TARGET_ACCOUNT_RECORD_MISSING: MemberActionBanner = {
  variant: "warn",
  title: "This clinician's account record is incomplete",
  body: "Part of their account record is missing, so the change wasn't applied. Retrying won't fix it — contact CITS support with this clinician's details so the record can be repaired.",
};

// ORG-012 — the address already identifies an account platform-wide (including
// the target's own current address: a no-op change is "in use", by them).
const EMAIL_IN_USE: MemberActionBanner = {
  variant: "warn",
  title: "That email address is already in use",
  body: "Another account already signs in with this address, so the email wasn't changed. Check the address and try a different one.",
};

// ORG-014 — the reset is on hold because this clinician's sign-in email was
// changed inside the cooldown window AND they have signed in at least once
// (gh-715's second leg; a never-used account is released instead, so this 409
// never arrives for one). `info`, not `warn`: nothing went wrong and
// nothing needs re-checking — the control is doing its job, and the only thing
// the admin can do is wait or escalate. The window LENGTH is deliberately not
// interpolated here: it is env-configurable server-side, and a client that
// hardcoded "24 hours" would start lying the moment an environment shortens it.
const EMAIL_CHANGE_COOLDOWN: MemberActionBanner = {
  variant: "info",
  title: "This reset is on hold",
  body: "This clinician's sign-in email was changed recently. For their security a password reset has to wait a while after an address change. If they can't sign in, contact CITS support.",
};

// 422 ORG-015 — the address parsed as RFC 5322 but is not a valid sign-in
// identifier. Distinct from the generic 422 below: that one points at "the
// values you entered" across a three-field form, while this refusal has exactly
// one culprit and a rule the admin can act on. Copy comes from the shared
// module so this reads identically to the Add-person form's inline refusal —
// same backend rule, raised by a second slice under the same code.
const INVALID_EMAIL_FORMAT: MemberActionBanner = {
  variant: "warn",
  title: INVALID_SIGN_IN_EMAIL_TITLE,
  body: INVALID_SIGN_IN_EMAIL_BODY,
};

// 429 ORG-013 — the per-action sliding window. All four member actions carry one
// now: DW-224 metered update-details and remove-seat too, which send no mail —
// what carries those two is the durable audit row a REFUSED call writes, not an
// email side-effect. Each action holds its OWN budget, so the copy stays about
// "changes" in general rather than naming one of them. Reassure rather than
// alarm, and leave the control usable later.
const RATE_LIMITED: MemberActionBanner = {
  variant: "info",
  title: "You're making changes quickly",
  body: "You've made several member changes in a short window. Please wait a little while before making another.",
};

// The wait is named whenever the server sent one, and phrased exactly as the
// self-service twin phrases it (`change-email-error.ts`): seconds up to 90s,
// whole minutes above. Absent or unusable values keep the generic copy — the
// null contract of `readRetryAfterSeconds` is "naming a wait would be inventing
// one".
function rateLimitedBanner(details: unknown): MemberActionBanner {
  const seconds = readRetryAfterSeconds(details);
  if (seconds === null) return RATE_LIMITED;
  const wait = seconds <= 90 ? `${Math.ceil(seconds)} seconds` : formatWaitMinutes(seconds);
  return {
    ...RATE_LIMITED,
    body: `You've made several member changes in a short window. Please try again in about ${wait}.`,
  };
}

// 422 — the payload failed schema validation. The modal's own inputs are the
// thing to look at; no field-level attach here, because the two forms this
// mapper serves already validate their single/three inputs client-side.
const INVALID_INPUT: MemberActionBanner = {
  variant: "warn",
  title: "Check the details",
  body: "Please review the values you entered and try again.",
};

// 403 with no recognized code — the actor lost the capability. Deterministic
// account state; no retry.
const FORBIDDEN: MemberActionBanner = {
  variant: "warn",
  title: "You can't make this change",
  body: "Your account isn't permitted to administer clinicians in this organization.",
};

/** Map any Edit-member write failure to the banner the modal renders. */
export function toMemberActionError(error: unknown): MemberActionBanner {
  if (error instanceof ApiError) {
    // Coded branches FIRST — ORG-010/008/009 are all 403s and ORG-011/012 are
    // both 409s, so a status-first order would collapse each pair into one
    // wrong explanation.
    if (error.code === "ORG-008") return OUTSIDE_ORG_SCOPE;
    if (error.code === "ORG-009") return INSUFFICIENT_TARGET_SCOPE;
    if (error.code === "ORG-010") return CONTRACT_INACTIVE;
    if (error.code === "ORG-011") return TARGET_NOT_ACTIVATED;
    if (error.code === "ORG-012") return EMAIL_IN_USE;
    if (error.code === "ORG-014") return EMAIL_CHANGE_COOLDOWN;
    if (error.code === "ORG-015") return INVALID_EMAIL_FORMAT;
    // ORG-017 sits with the coded branches, ahead of the status fall-throughs,
    // like every other code here. It answers 409 rather than 403 precisely so a
    // bundle predating this line degrades to the neutral generic banner instead
    // of `FORBIDDEN`, whose copy blames the ACTOR for a fact about the target.
    if (error.code === "ORG-017") return TARGET_BLOCKED;
    // ORG-019 sits immediately beside ORG-017 because it is the refusal that
    // used to arrive AS ORG-017 — keeping them adjacent is what makes a future
    // edit that collapses one into the other visible in the diff.
    if (error.code === "ORG-019") return TARGET_ACCOUNT_RECORD_MISSING;
    if (error.status === 429) return rateLimitedBanner(error.details);
    if (error.status === 422) return INVALID_INPUT;
    if (error.status === 403) return FORBIDDEN;
    // Any other coded error falls through to the generic danger banner.
  }
  // NETWORK_ERROR / AuthExpiredError / unknown → generic, no internals leaked.
  return GENERIC;
}
